import { cloneElement, isValidElement } from 'react';
import { PanelDefinition } from '@/core';
import {
  doesPluginSupportDataType,
  getSupportedDataType,
  useListPluginMetadata,
  usePlugin,
} from '@/plugin-system/runtime';

type MaybeHasPanelDefinition = {
  panelDefinition?: PanelDefinition;
};

/**
 * Clones the children to include the panelDefinition if they have `panelDefinition` in the props and
 * it is undefined.
 */
export function maybeClonePanelDefinition(children: React.ReactNode, panelDefinition?: PanelDefinition, key?: string) {
  if (isValidElement<MaybeHasPanelDefinition>(children) && children.props.panelDefinition === undefined) {
    return cloneElement(children, { panelDefinition, key });
  }

  return children;
}

/**
 * Checks if the queries in a panel definition are compatible with the data type required by the panel plugin.
 * Returns true if all queries are compatible with the required data type, otherwise false.
 */
export function useAreQueriesCompatibleWithDataType(panelDefinition?: PanelDefinition) {
  const panelPluginDefinition = panelDefinition?.spec.plugin;
  const queries = panelDefinition?.spec.queries;
  const { data: panelPlugin, isLoading } = usePlugin('Panel', panelPluginDefinition?.kind ?? '');
  const { data: dataQueryPluginMetadata, isLoading: isMetadataLoading } = useListPluginMetadata('DataQuery');

  // The panel definition is optional when developers use react components directly.
  // In this case, we return true because the context is unclear.
  if (!panelPluginDefinition) return true;

  // Return true to avoid showing an error while the plugin is still loading
  if (isLoading || isMetadataLoading) return true;

  const dataType = getSupportedDataType(panelPluginDefinition.spec, panelPlugin);
  if (queries) {
    for (let i = 0; i < queries.length; i++) {
      const queryDefinition = queries[i];
      if (!queryDefinition) continue;
      const { kind: queryKind } = queryDefinition.spec.plugin;
      const pluginMetadata = dataQueryPluginMetadata?.find((pluginMetadata) => pluginMetadata.kind === queryKind);
      const isDatasourceCompatibleWithDataType = doesPluginSupportDataType({
        plugin: pluginMetadata,
        validDataType: dataType,
      });
      if (!isDatasourceCompatibleWithDataType) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Prepends text to panelKey to use in the DOM as a unique identifier.
 * Reused in share panel logic since formatted panelKey must match.
 */
export const getFormattedPanelKey = (panelKey?: string) => (panelKey ? `panel-${panelKey}` : undefined);
